import React, { Fragment, FunctionComponent, useEffect, useState } from 'react';
import { Box, CircularProgress, useMediaQuery } from '@material-ui/core';
import { createStyles, makeStyles, Theme, useTheme } from '@material-ui/core/styles';
import { Booking } from '@spike/booking-model';
import moment, { Moment } from 'moment-timezone';
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';
import CreateBookingHeader from './CreateBookingHeader';
import FooterComponent from './FooterComponent';
import ClientSearch from './ClientSearch/ClientSearch';
import PetSearch from './PetSearch';
import { Option } from '@spike/model';
import DateSelector from './DateSelector';
import {
    NewBookingAlert,
    NewBookingAppointment,
    NewBookingOrder,
    NewBookingOrderItems,
    NewBookingOrderizable,
    PetService
} from '@spike/new-booking-model';
import { useApiClientWrapper, useAuth, useMarketplace, useTimeZone } from 'hooks';
import DepositSelector from './DepositSelector';
import Notes from './Notes';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import {
    fetchAlertsThunk,
    fetchPetServicesThunk,
    NewBookingsStatus,
    saveMultipleBookingForDepositThunk,
    saveMultipleBookingThunk
} from '@spike/new-bookings-action';
import useNonInitialEffect from '@versiondos/hooks';
import { AppointmentsStatus, BookingsStatus, deleteBookingThunk } from '@spike/bookings-action';
import CollectDeposit from './CollectDeposit';
import AppointmentCreated from './UI/AppointmentCreated';
import ConfirmClosePopup from './UI/ConfirmClosePopup';
import clsx from 'clsx';
import * as amplitude from '@amplitude/analytics-browser';
import { AMPLITUDE } from 'constants/index';
import { MarketplaceDepositType } from '@spike/marketplace-model';
import { convertToNumber } from '@spike/price-utils';
import { AppointmentCountRecurrent } from '@spike/appointment-model';
import { fetchPetsThunk, PetsState } from '@spike/pets-action';
import MultiplePets from './MultiplePets';
import PetAndServices from './PetAndServices';
import { ActionsPopup } from 'components/UI/ActionsPopup/ActionsPopup';
import { faPencil } from '@fortawesome/pro-light-svg-icons';
import { faTrashAlt } from '@fortawesome/pro-regular-svg-icons';
import { Spinner } from 'components/UI';
import ComplementaryItem from './ExtraItems';
import {
    AppointmentActions,
    DataVaraible,
    AppointmentMultiple,
    itemTypes,
    PetServiceTotal,
    PetServiceWrapper,
    versionPetAndServices,
    wizardBooking,
    Discount,
    OrderizableTypes,
    ItemData,
    AddOnDate
} from 'model/NewMultipleBooking';
import AddOnService from '@spike/addon-service-model';
import { Product } from '@spike/product-model';
import OrderDetail from 'components/OrderDetail';
import Checkout from 'components/Checkout2';
import { AppointmentOrderItem, Order } from '@spike/order-model';
import { fetchServicesThunk } from 'actions/services/ServicesActions';
import { ServicesStatus } from 'reducers/services/ServicesState';
import PetCard from 'components/PetCard';

interface CreateBookingProps {
    fromClient?: boolean;
    staffId?: number;
    at?: Moment;
    parentID?: number;
    petID?: number;
    parentName?: string;
    appointmentId?: number;
    order?: Order;
    appointmentItem?: AppointmentOrderItem;
    detailView?: boolean;
    clientAdded?: boolean;
    petAdded?: boolean;
    petsWithoutSelect?: boolean;
    multipleAppointments?: Array<AppointmentMultiple>;
    onAddClient?: () => void;
    onAddPet?: (clientId: number) => void;
    onSave?: () => void;
    onClose: (value?: boolean) => void;
    onBooked: () => void;
    onStartBooking?: () => void;
    onChangeTime?: (bookedAt?: Moment, durationInMinutes?: number, staffId?: number) => void;
    onRemoveClient?: () => void;
    onShowBlockedTime?: () => void;
    onChangeNewBooking?: (value: boolean) => void;
    onShowPetCard?: (petId: number, clientId: number) => void;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        container: {
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            height: '100%'
        },
        headerContainer: {
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            margin: '17px 27px 27px 27px',
            [theme.breakpoints.down('sm')]: {
                margin: '20px 30px 30px 30px'
            },
            [theme.breakpoints.up('md')]: {
                margin: '20px 30px 27px 30px'
            }
        },
        appointmentContainer: {
            margin: '0px 26px 0px 26px',
            [theme.breakpoints.up('md')]: {
                margin: '0px 30px 0px 30px'
            }
        },
        totalContainer: {
            height: '15%',
            borderTop: '1px solid #D4D4D4'
        },
        marginTotalContainer: {
            marginTop: 11,
            marginBottom: 22
        },
        sectionContainer: {
            paddingTop: 18,
            [theme.breakpoints.up('md')]: {
                paddingTop: 20
            }
        },
        appointmentCreatedContainer: {
            height: '87%'
        },
        withoutEvents: {
            pointerEvents: 'none'
        },
        marginTop: {
            marginTop: -20
        },
        loadingContainer: {
            width: '100%',
            display: 'flex',
            height: '100%',
            justifyContent: 'center',
            alignItems: 'center'
        }
    })
);

export const CreateBooking: FunctionComponent<CreateBookingProps> = props => {
    const classes = useStyles();
    const timezone = useTimeZone();
    const apiClientWrapper = useApiClientWrapper();
    const auth = useAuth();
    const marketplace = useMarketplace();
    const dispatch = useDispatch();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const alerts = useSelector<RootState, Array<NewBookingAlert> | undefined>(state => state.newBookings.alerts);
    const newBookingsStatus = useSelector<RootState, NewBookingsStatus>(state => state.newBookings.status);
    const savedBooking = useSelector<RootState, Booking | undefined>(state => state.newBookings.bookingSavedForDeposit);
    const appointmentsStatus = useSelector<RootState, AppointmentsStatus>(state => state.appointments.status);
    const petsState = useSelector<RootState, PetsState>(state => state.pets);
    const recurrentInfo = useSelector<RootState, undefined | AppointmentCountRecurrent>(
        state => state.appointments.recurrent
    );
    const bookingStatus = useSelector<RootState, BookingsStatus>(state => state.bookings.status);
    const petServices = useSelector<RootState, Array<PetService>>(state => state.newBookings.petServices);
    const serviceStatus = useSelector<RootState, ServicesStatus>(state => state.services.status);

    const [parentId, setParentId] = useState<number | undefined>(props.parentID);
    const [parentName, setParentName] = useState<string | undefined>(props.parentName);
    const [petId, setPetId] = useState<number | undefined>(props.petID);
    const [activeFrequency, setActiveFrequency] = useState(false);
    const [frequency, setFrequency] = useState<Option<string> | undefined>(undefined);
    const [service, setService] = useState<PetServiceWrapper | undefined>();
    const [date, setDate] = useState<Moment>(props.at ? props.at : moment().tz(timezone));
    const [dateToEdit, setDateToEdit] = useState<Moment | undefined>(undefined);
    const [staffId, setStaffId] = useState<number | undefined>();
    const [showDepositAndNote, setShowDepositAndNote] = useState(false);
    const [isCollectDeposit, setIsCollectDeposit] = useState(false);
    const [notes, setNotes] = useState('');
    const [saving, setSaving] = useState(false);
    const [depositCollect, setDepositCollect] = useState(false);
    const [appointmentCreated, setAppointmentCreated] = useState(false);
    const [closing, setClosing] = useState({ close: false, tabChange: false });
    const [loading, setLoading] = useState<boolean>(false);
    const [touched, setTouched] = useState(false);
    const [showPetAndServices, setShowPetAndServices] = useState(false);
    const [showComplementaryItem, setShowComplementaryItem] = useState(false);
    const [versionPetAndService, setVersionPetAndService] = useState<versionPetAndServices | undefined>(undefined);
    const [multipleAppointments, setMultipleAppointments] = useState<Array<AppointmentMultiple>>(
        props.multipleAppointments || []
    );
    const [serviceTotal, setServiceTotal] = useState<PetServiceTotal | undefined>();
    const [exceededDeposit, setExceededDeposit] = useState<boolean>(
        marketplace.deposit?.type === MarketplaceDepositType.Amount &&
            convertToNumber(marketplace.deposit?.value || '0') > (serviceTotal?.price || service?.price || 0)
    );
    const [showAlertBusinessHour, setShowAlertBusinessHour] = useState<boolean>(false);
    const [showActions, setShowActions] = useState<AppointmentActions>({
        date: undefined,
        staffId: undefined,
        service: undefined,
        petId: undefined,
        product: undefined,
        addOn: undefined,
        custom: undefined,
        type: undefined,
        discount: undefined,
        itemData: undefined
    });
    const [loadingAppointments, setLoadingAppointments] = useState<boolean>(false);
    const [rebook, setRebook] = useState<boolean>(false);
    const [type, setType] = useState<itemTypes | undefined>(undefined);
    const [discount, setDiscount] = useState<Discount>({
        type: 'amount',
        value: 0
    });
    const [product, setProduct] = useState<Product | undefined>(undefined);
    const [addOn, setAddOn] = useState<AddOnService | undefined>(undefined);
    const [custom, setCustom] = useState<string | undefined>(undefined);
    const [servicesUsed, setServicesUsed] = useState<Array<number>>([]);
    const [petsUsed, setPetsUsed] = useState<Array<number>>([]);
    const [quantity, setQuantity] = useState<number>(1);
    const [amount, setAmount] = useState<number>(0);
    const [orderData, setOrderData] = useState<{
        orderId?: number;
        bookingId?: number;
        showDetail: boolean;
        showCheckout: boolean;
    }>({
        showDetail: false,
        showCheckout: false
    });
    const [datesAddOn, setDatesAddOn] = useState<Array<AddOnDate> | undefined>(undefined);
    const [itemsUsed, setItemsUsed] = useState<Array<number>>([]);
    const [showPetCard, setShowPetCard] = useState<{ petId: number | undefined }>({
        petId: undefined
    });

    const handlerRemove = (value: string) => {
        switch (value) {
            case wizardBooking.PetParent:
                setParentId(undefined);
                setParentName('');
                setParentId(undefined);
                setPetId(undefined);
                setShowDepositAndNote(false);
                setMultipleAppointments([]);
                break;
            case wizardBooking.Service:
                setService(undefined);
                setDiscount({
                    type: 'amount',
                    value: 0
                });
                break;
            case wizardBooking.Staff:
                setStaffId(undefined);
                break;
            case wizardBooking.Deposit:
                setIsCollectDeposit(false);
                break;
            case wizardBooking.Notes:
                setNotes('');
                break;
        }
    };

    const handleRemove = () => {
        handlerRemove(wizardBooking.PetParent);
        handlerRemove(wizardBooking.Service);
        handlerRemove(wizardBooking.Staff);
        handlerRemove(wizardBooking.Deposit);
        handlerRemove(wizardBooking.Notes);
        setOrderData(prev => ({ ...prev, showDetail: false, showCheckout: false }));
    };

    const handleCloseBooking = (value?: boolean) => {
        props.onClose(value);
    };

    const handleCloseWithOutModal = () => {
        props.onClose(true);
    };

    const handleBookedBooking = () => {
        handleRemove();
        props.onBooked();
    };

    const handleBooked = () => {
        setSaving(true);
        if (isCollectDeposit) {
            amplitude.track(AMPLITUDE.CTA_PAY_AND_BOOK_APPOINTMENT);
        } else {
            amplitude.track(AMPLITUDE.CTA_BOOK_APPOINTMENT);
        }

        if (appointmentCreated) {
            handleBookedBooking();
        } else if (isCollectDeposit && !exceededDeposit) {
            const multipleBookingFormatted: Array<NewBookingAppointment> = getMultipleBookingToSave();
            const complementaryItemsFormatted: NewBookingOrder = getComplementaryItemsToSave();
            dispatch(
                saveMultipleBookingForDepositThunk(
                    apiClientWrapper,
                    multipleBookingFormatted,
                    complementaryItemsFormatted
                )
            );
        } else {
            const multipleBookingFormatted: Array<NewBookingAppointment> = getMultipleBookingToSave();
            const complementaryItemsFormatted: NewBookingOrder = getComplementaryItemsToSave();
            dispatch(
                saveMultipleBookingThunk(
                    apiClientWrapper,
                    multipleBookingFormatted,
                    complementaryItemsFormatted,
                    frequency?.id
                )
            );
        }
    };

    const haveMultiplePets = () => {
        return (
            multipleAppointments.length > 1 ||
            (multipleAppointments.length === 1 && multipleAppointments[0].variableData.length > 1)
        );
    };

    const handlerSavePetAndServices = (
        dateAppt?: Moment,
        petIdAppt?: number,
        serviceAppt?: PetServiceWrapper,
        staffIdAppt?: number,
        discountAppt?: Discount
    ) => {
        setShowPetAndServices(false);
        setShowComplementaryItem(false);
        setVersionPetAndService(undefined);
        backPetAndServicesOrComplementaryItem();
        setType(itemTypes.service);
        const petFounded = multipleAppointments.findIndex((app: AppointmentMultiple) => app.petId === petId);
        if (petFounded !== -1) {
            const newMultipleAppointments = [...multipleAppointments];
            if (versionPetAndService === versionPetAndServices.edit) {
                const serviceFounded = multipleAppointments[petFounded]?.variableData?.findIndex(
                    (app: DataVaraible) => app.service?.service?.id === service?.service?.id
                );
                if (serviceFounded !== -1) {
                    newMultipleAppointments[petFounded].variableData[serviceFounded] = {
                        service: serviceAppt!,
                        staffId: staffIdAppt!,
                        date: dateAppt || date,
                        itemData: {
                            discount: {
                                type: discountAppt?.type || 'amount',
                                value: discountAppt?.value ?? 0
                            }
                        }
                    };
                    setMultipleAppointments(newMultipleAppointments);
                }
            } else {
                newMultipleAppointments[petFounded] = {
                    ...newMultipleAppointments[petFounded],
                    variableData: [
                        ...newMultipleAppointments[petFounded].variableData,
                        {
                            service: serviceAppt!,
                            staffId: staffIdAppt!,
                            date: dateAppt || date,
                            itemData: {
                                discount: {
                                    type: discountAppt?.type || 'amount',
                                    value: discountAppt?.value ?? 0
                                }
                            }
                        }
                    ]
                };
                setMultipleAppointments(newMultipleAppointments);
            }
        } else {
            const appointmentToSave: AppointmentMultiple = {
                clientId: parentId!,
                petId: petIdAppt!,
                createdByStaffId: auth.user!.staffId,
                variableData: [
                    {
                        service: serviceAppt!,
                        staffId: staffIdAppt!,
                        date: dateAppt || date,
                        itemData: {
                            discount: {
                                type: discountAppt?.type || 'amount',
                                value: discountAppt?.value ?? 0
                            }
                        }
                    }
                ],
                notes: notes
            };
            setMultipleAppointments(prev => [...prev, appointmentToSave]);
        }
    };

    const handlerRemoveAppointments = (petId: number | undefined, service: PetServiceWrapper | undefined) => {
        setShowPetAndServices(false);
        setShowComplementaryItem(false);
        setVersionPetAndService(undefined);
        backPetAndServicesOrComplementaryItem();
        const petFounded = multipleAppointments.findIndex((app: AppointmentMultiple) => app.petId === petId);
        if (petFounded !== -1) {
            const appointmentsWithoutApptRemoved = multipleAppointments[petFounded]?.variableData?.filter(
                (appt: DataVaraible) => appt.service?.service.id !== service?.service.id
            );
            const newMultipleAppointments = [...multipleAppointments];
            newMultipleAppointments[petFounded] = {
                ...newMultipleAppointments[petFounded],
                variableData: appointmentsWithoutApptRemoved
            };

            if (appointmentsWithoutApptRemoved?.length === 0) {
                const appointmentsWithoutThisPet = newMultipleAppointments.filter(
                    (app: AppointmentMultiple) => app.petId !== petId
                );
                setMultipleAppointments(appointmentsWithoutThisPet);
            } else {
                setMultipleAppointments(newMultipleAppointments);
            }
        }
    };

    const getIndexOfItem = (array: Array<DataVaraible>) => {
        const items = {
            [itemTypes.service]: 0,
            'add-on': array.findIndex((item: DataVaraible) => item.addOn?.id === addOn?.id),
            [itemTypes.product]: array.findIndex(
                (item: DataVaraible) => item.product?.businessProduct?.id === product?.businessProduct?.id
            ),
            [itemTypes.custom]: array.findIndex((item: DataVaraible) => item.customDescription === custom)
        };
        return items[type as itemTypes] || -1;
    };

    const handlerSaveItems = (
        date?: Moment,
        petId?: number,
        selectedProduct?: Product,
        selectedAddOn?: AddOnService,
        custom?: string,
        staffId?: number,
        discount?: Discount,
        quantity?: number,
        amount?: number
    ) => {
        setShowPetAndServices(false);
        setShowComplementaryItem(false);
        setVersionPetAndService(undefined);
        backPetAndServicesOrComplementaryItem();
        if (versionPetAndService === versionPetAndServices.add) {
            const petFounded = multipleAppointments.findIndex((app: AppointmentMultiple) => app.petId === petId);
            if (petFounded !== -1) {
                const newMultipleAppointments = [...multipleAppointments];
                newMultipleAppointments[petFounded] = {
                    ...newMultipleAppointments[petFounded],
                    variableData: [
                        ...newMultipleAppointments[petFounded].variableData,
                        {
                            date: date!,
                            staffId: staffId,
                            product: type === itemTypes.product ? selectedProduct : undefined,
                            addOn: type === itemTypes.addOn ? selectedAddOn : undefined,
                            customDescription: type === itemTypes.custom ? custom : undefined,
                            itemData: {
                                discount: {
                                    type: discount?.type || 'amount',
                                    value: discount?.value ?? 0
                                },
                                quantity: quantity,
                                price: amount,
                                total: quantity && amount ? parseFloat((quantity * amount).toFixed(2)) : 0
                            }
                        }
                    ]
                };
                setMultipleAppointments(newMultipleAppointments);
            }
        } else if (versionPetAndService === versionPetAndServices.edit) {
            const petFounded = multipleAppointments.findIndex((app: AppointmentMultiple) => app.petId === petId);
            if (petFounded !== -1) {
                const newMultipleAppointments = [...multipleAppointments];
                const appt: AppointmentMultiple = newMultipleAppointments[petFounded];
                const elementEdited: DataVaraible = {
                    date: date!,
                    staffId: staffId,
                    product: type === itemTypes.product ? selectedProduct : undefined,
                    addOn: type === itemTypes.addOn ? selectedAddOn : undefined,
                    customDescription: type === itemTypes.custom ? custom : undefined,
                    itemData: {
                        discount: {
                            type: discount?.type || 'amount',
                            value: discount?.value ?? 0
                        },
                        quantity: quantity,
                        price: amount,
                        total: quantity && amount ? parseFloat((quantity * amount).toFixed(2)) : 0
                    }
                };
                const index = getIndexOfItem(appt.variableData);
                if (index !== -1) {
                    newMultipleAppointments[petFounded].variableData[index] = elementEdited;
                }
                if (newMultipleAppointments[petFounded]?.variableData.length === 0) {
                    const appointmentsWithoutThisPet = newMultipleAppointments.filter(
                        (app: AppointmentMultiple) => app.petId !== petId
                    );
                    setMultipleAppointments(appointmentsWithoutThisPet);
                } else {
                    setMultipleAppointments(newMultipleAppointments);
                }
            }
        }
    };

    const getArrayWithItemsRemoved = (array: Array<DataVaraible>, dateToRemove?: Moment) => {
        const items = {
            [itemTypes.service]: array,
            'add-on': array.filter(
                (item: DataVaraible) =>
                    item.addOn?.id !== addOn?.id || (item.addOn?.id === addOn?.id && item.date !== dateToRemove)
            ),
            [itemTypes.product]: array.filter(
                (item: DataVaraible) => item.product?.businessProduct?.id !== product?.businessProduct?.id
            ),
            [itemTypes.custom]: array.filter((item: DataVaraible) => item.customDescription !== custom)
        };
        return items[type as itemTypes] || array;
    };

    const handlerRemoveItems = (dateToRemove?: Moment) => {
        setShowPetAndServices(false);
        setShowComplementaryItem(false);
        setVersionPetAndService(undefined);
        backPetAndServicesOrComplementaryItem();
        const petIndex = multipleAppointments.findIndex((app: AppointmentMultiple) => app.petId === petId);
        if (petIndex !== -1) {
            const appointmentsWithoutApptRemoved = getArrayWithItemsRemoved(
                multipleAppointments[petIndex].variableData,
                dateToRemove
            );
            const newMultipleAppointments = [...multipleAppointments];
            newMultipleAppointments[petIndex] = {
                ...newMultipleAppointments[petIndex],
                variableData: appointmentsWithoutApptRemoved
            };
            if (appointmentsWithoutApptRemoved?.length === 0) {
                const appointmentsWithoutThisPet = newMultipleAppointments.filter(
                    (app: AppointmentMultiple) => app.petId !== petId
                );
                setMultipleAppointments(appointmentsWithoutThisPet);
            } else {
                setMultipleAppointments(newMultipleAppointments);
            }
        }
    };

    const handlerParentID = (petParentId: number) => {
        if (petParentId !== 0) {
            setParentId(petParentId);
            dispatch(fetchPetsThunk(apiClientWrapper, petParentId));
            amplitude.track(AMPLITUDE.LOAD_CLIENT_PETS);
        } else {
            handlerRemove(wizardBooking.PetParent);
            handlerRemove(wizardBooking.Service);
            handlerRemove(wizardBooking.Staff);
            handlerRemove(wizardBooking.Deposit);
            handlerRemove(wizardBooking.Notes);
            props.onRemoveClient && props.onRemoveClient();
        }
    };

    const handleFrequencyDuration = (durationId: Option<string> | undefined, frequencyId: Option<string>) => {
        setFrequency(frequencyId);
    };

    const addNewClient = () => {
        props.onAddClient && props.onAddClient();
    };

    const backHandler = () => {
        setSaving(true);
        setDepositCollect(false);
        setOrderData(prev => ({ ...prev, showDetail: false }));
        dispatch(deleteBookingThunk(apiClientWrapper, savedBooking!.id!, true));
    };

    const backPetAndServicesOrComplementaryItem = () => {
        setPetId(undefined);
        setService(undefined);
        setStaffId(undefined);
        setDiscount({
            type: 'amount',
            value: 0
        });
        setShowPetAndServices(false);
        setShowComplementaryItem(false);
        setVersionPetAndService(undefined);
    };

    const handlerCollected = () => {
        setDepositCollect(false);
        setAppointmentCreated(true);
        props.onChangeNewBooking && props.onChangeNewBooking(false);
    };

    const changeTabHandler = () => {
        if (touched) {
            setClosing(prev => ({ ...prev, tabChange: true }));
        } else {
            props.onShowBlockedTime && props.onShowBlockedTime();
        }
    };

    const handleClose = (value?: boolean) => {
        closing.close && handleCloseBooking(value);
        closing.tabChange && props.onShowBlockedTime && props.onShowBlockedTime();
    };

    const handleClosing = () => {
        setClosing(prev => ({
            ...prev,
            close: true
        }));
        setOrderData(prev => ({ ...prev, showDetail: false }));
    };

    const handleChangeRecurrency = (value: boolean) => {
        setActiveFrequency(value);
        if (!value) {
            setFrequency(undefined);
        }
    };

    const handleChangeDeposit = (value: boolean) => {
        setIsCollectDeposit(value);
        if (value) {
            setActiveFrequency(false);
            setFrequency(undefined);
            amplitude.track(AMPLITUDE.ENABLE_BOOKING_DEPOSIT);
        } else {
            amplitude.track(AMPLITUDE.DISABLE_BOOKING_DEPOSIT);
        }
    };

    const handlerAddAnotherPet = () => {
        setVersionPetAndService(versionPetAndServices.another);
        setShowPetAndServices(true);
    };

    const handlerAddItem = (value: number, type?: itemTypes) => {
        setType(type);
        setPetId(value);
        setVersionPetAndService(versionPetAndServices.add);
        if (type === itemTypes.service) {
            setShowPetAndServices(true);
        } else {
            setShowComplementaryItem(true);
        }
    };

    const handlerDeleteAppointmentsPet = (value: number) => {
        const multiplePetFiltered: Array<AppointmentMultiple> = multipleAppointments.filter(
            (appt: AppointmentMultiple) => appt.petId !== value
        );
        setMultipleAppointments(multiplePetFiltered);
    };

    const handlerEditAppointmentPet = (
        petId: number,
        dateEdit: Moment,
        staffId?: number,
        itemData?: ItemData,
        service?: PetServiceWrapper,
        product?: Product,
        addOn?: AddOnService,
        custom?: string,
        type?: itemTypes
    ) => {
        setPetId(petId);
        setStaffId(staffId);
        setDateToEdit(dateEdit);
        setDiscount(itemData?.discount!);
        if (service) {
            setService(service);
            setShowPetAndServices(true);
        } else {
            setShowComplementaryItem(true);
            if (product) {
                setProduct(product);
            } else if (addOn) {
                setAddOn(addOn);
            } else {
                setCustom(custom);
            }
            setQuantity(itemData?.quantity || 1);
            setAmount(itemData?.price || 0);
        }
        setType(type);
        setVersionPetAndService(versionPetAndServices.edit);
        setShowActions({
            date: undefined,
            staffId: undefined,
            service: undefined,
            petId: undefined,
            product: undefined,
            addOn: undefined,
            custom: undefined,
            type: undefined,
            discount: undefined,
            itemData: undefined
        });
    };

    const getArrayWithItemRemoved = (
        array: Array<DataVaraible>,
        type: itemTypes,
        value?: string | number,
        dateToRemove?: Moment
    ) => {
        const items = {
            [itemTypes.service]: array.filter((appt: DataVaraible) => appt.service?.service.id !== value),
            'add-on': array.filter(
                (appt: DataVaraible) =>
                    appt.addOn?.id !== value || (appt.addOn?.id === value && appt.date !== dateToRemove)
            ),
            [itemTypes.product]: array.filter((appt: DataVaraible) => appt.product?.id !== value),
            [itemTypes.custom]: array.filter((appt: DataVaraible) => appt.customDescription !== value)
        };
        return items[type];
    };

    const handlerDeleteAppointmentPet = (
        petId: number,
        value?: number | string,
        type?: itemTypes,
        dateToRemove?: Moment
    ) => {
        const petIndex = multipleAppointments.findIndex((app: AppointmentMultiple) => app.petId === petId);
        if (petIndex !== -1) {
            const appointmentsWithoutApptRemoved = getArrayWithItemRemoved(
                multipleAppointments[petIndex].variableData,
                type!,
                value,
                dateToRemove
            );
            const newMultipleAppointments = [...multipleAppointments];
            newMultipleAppointments[petIndex] = {
                ...newMultipleAppointments[petIndex],
                variableData: appointmentsWithoutApptRemoved
            };
            if (appointmentsWithoutApptRemoved?.length === 0) {
                const appointmentsWithoutThisPet = newMultipleAppointments.filter(
                    (app: AppointmentMultiple) => app.petId !== petId
                );
                setMultipleAppointments(appointmentsWithoutThisPet);
            } else {
                setMultipleAppointments(newMultipleAppointments);
            }
            setShowActions({
                date: undefined,
                staffId: undefined,
                service: undefined,
                petId: undefined,
                product: undefined,
                addOn: undefined,
                custom: undefined,
                type: undefined,
                discount: undefined,
                itemData: undefined
            });
            setLoadingAppointments(true);
        }
    };

    const getMultipleBookingToSave = (): Array<NewBookingAppointment> => {
        const multipleAppointmentsToSave: Array<NewBookingAppointment> = [];

        multipleAppointments.forEach((appt: AppointmentMultiple) => {
            appt.variableData.forEach((data: DataVaraible) => {
                if (data.service) {
                    multipleAppointmentsToSave.push({
                        petId: appt.petId,
                        serviceId: data.service?.service.id!,
                        orderItemPrice: data.service?.price,
                        date: data.date,
                        notes: notes || '',
                        createdByStaffId: appt.createdByStaffId,
                        clientId: appt.clientId,
                        staffId: data.staffId,
                        dateEnd: data.date.clone().add(data.service?.durationInMinutes, 'minutes') || undefined,
                        discount:
                            data.itemData?.discount?.type !== 'none'
                                ? {
                                      amount:
                                          data.itemData?.discount?.type === 'amount'
                                              ? data.itemData?.discount?.value?.toString()
                                              : undefined,
                                      percentage:
                                          data.itemData?.discount?.type === 'percentage'
                                              ? data.itemData?.discount?.value
                                              : undefined
                                  }
                                : undefined
                    });
                }
            });
        });

        return multipleAppointmentsToSave;
    };

    const getType = (data: DataVaraible) => {
        let type = itemTypes.service;
        if (data.service) {
            type = itemTypes.service;
        } else if (data.product) {
            type = itemTypes.product;
        } else if (data.addOn) {
            type = itemTypes.addOn;
        } else if (data.customDescription) {
            type = itemTypes.custom;
        }
        return type;
    };

    const getOrderizable = (data: DataVaraible): NewBookingOrderizable => {
        const items = {
            [itemTypes.service]: {
                id: data.service?.service.id,
                type: OrderizableTypes.APPOINTMENT
            },
            'add-on': {
                id: data.addOn?.id,
                type: OrderizableTypes.ADDON_SERVICE
            },
            [itemTypes.product]: {
                id: data.product?.businessProduct?.id,
                type: OrderizableTypes.PRODUCT
            },
            [itemTypes.custom]: {
                id: undefined,
                type: undefined
            }
        };
        return items[getType(data)] || undefined;
    };

    const getDescription = (data: DataVaraible) => {
        const items = {
            [itemTypes.service]: data.service?.service?.name,
            'add-on': data.addOn?.name,
            [itemTypes.product]: data.product?.name,
            [itemTypes.custom]: data.customDescription
        };
        return items[getType(data)] || undefined;
    };

    const getComplementaryItemsToSave = (): NewBookingOrder => {
        const items: Array<NewBookingOrderItems> = [];

        multipleAppointments.forEach((appt: AppointmentMultiple) => {
            appt.variableData.forEach((data: DataVaraible) => {
                if (!data.service) {
                    items.push({
                        orderizable: getOrderizable(data),
                        staffId: data.staffId,
                        description: getDescription(data),
                        petId: appt.petId,
                        quantity: data.itemData?.quantity || 0,
                        amount: data.itemData?.price?.toString() || '0',
                        startedAt: data.date,
                        discount: {
                            percentage:
                                data.itemData?.discount?.type === 'percentage'
                                    ? data.itemData?.discount?.value
                                    : undefined,
                            amount:
                                data.itemData?.discount?.type === 'amount'
                                    ? data.itemData?.discount?.value?.toString()
                                    : undefined
                        }
                    });
                }
            });
        });

        return {
            customerId: multipleAppointments[0].clientId,
            shipTocustomerId: multipleAppointments[0].clientId,
            notes: notes || '',
            issuedAt: moment().tz(timezone),
            items: items
        };
    };

    const withoutService = () => {
        let withoutService = true;
        multipleAppointments?.forEach((appt: AppointmentMultiple) => {
            appt.variableData?.forEach((data: DataVaraible) => {
                if (data.service) {
                    withoutService = false;
                }
            });
        });
        return withoutService;
    };

    const getNameItem = (type: itemTypes) => {
        const items = {
            [itemTypes.service]: 'Edit Service',
            'add-on': 'Edit Add On',
            [itemTypes.product]: 'Edit Product',
            [itemTypes.custom]: 'Edit Custom Item'
        };
        return items[type] || '';
    };

    const getItemToDelete = (type: itemTypes) => {
        const items = {
            [itemTypes.service]: showActions.service?.service?.id,
            'add-on': showActions.addOn?.id,
            [itemTypes.product]: showActions.product?.id,
            [itemTypes.custom]: showActions.custom
        };
        return items[type] || '';
    };

    const handlerChangeGeneralDate = (date: Moment) => {
        setDate(date);
        const multipleAppointmentsCopy = [...multipleAppointments];
        multipleAppointmentsCopy.forEach(app => {
            app.variableData?.forEach(data => {
                data.date = date;
            });
        });
        setMultipleAppointments(multipleAppointmentsCopy);
    };

    const getPriceWithDiscount = (data: DataVaraible, total?: number) => {
        if (data.itemData?.discount?.type === 'percentage' && total) {
            const amountToDiscount = (data.itemData?.discount?.value * total) / 100;
            return total - amountToDiscount;
        } else if (data.itemData?.discount?.type === 'amount' && total) {
            return total - data.itemData?.discount?.value;
        }
    };

    const getServicesUsed = () => {
        const servicesUsedAux: Array<number> = [];
        multipleAppointments.forEach(app => {
            if (app.petId === petId) {
                app.variableData?.forEach(data => {
                    if (versionPetAndService === versionPetAndServices.edit) {
                        if (data.service?.service.id !== service?.service.id) {
                            servicesUsedAux.push(data.service?.service.id!);
                        }
                    } else {
                        servicesUsedAux.push(data.service?.service.id!);
                    }
                });
            }
        });
        return servicesUsedAux;
    };

    const getPetsUsed = () => {
        const petsUsedAux: Array<number> = [];
        multipleAppointments.forEach(app => {
            petsUsedAux.push(app.petId);
        });
        return petsUsedAux;
    };

    const getItemsUsed = () => {
        const itemsUsed: Array<number> = [];
        const dates: Array<{
            id?: number;
            date: string;
        }> = [];
        multipleAppointments.forEach(app => {
            if (type === itemTypes.addOn) {
                if (app.petId === petId) {
                    app.variableData?.forEach(data => {
                        if (versionPetAndService === versionPetAndServices.edit) {
                            if (data.addOn?.id && addOn?.id) {
                                if (
                                    data.addOn?.id !== addOn?.id ||
                                    (data.addOn?.id === addOn?.id && data.date?.format('L') && dateToEdit?.format('L'))
                                ) {
                                    itemsUsed.push(data.addOn?.id!);
                                }
                                dates.push({ id: data.addOn?.id, date: data.date?.format('L') });
                            }
                        } else {
                            if (data.addOn?.id) {
                                itemsUsed.push(data.addOn?.id!);
                                dates.push({ id: data.addOn?.id, date: data.date?.format('L') });
                            }
                        }
                    });
                }
            }
            if (type === itemTypes.product) {
                if (app.petId === petId) {
                    app.variableData?.forEach(data => {
                        if (versionPetAndService === versionPetAndServices.edit) {
                            if (data.product?.businessProduct?.id !== product?.businessProduct?.id) {
                                itemsUsed.push(data.product?.businessProduct?.id!);
                            }
                        } else {
                            itemsUsed.push(data.product?.businessProduct?.id!);
                        }
                    });
                }
            }
        });
        setDatesAddOn(dates);
        return itemsUsed;
    };

    const getUniquePetIds = (order: Order): Array<number> => {
        const petIds = new Set<number>();

        order.items.appointments.forEach(item => {
            if (item.pet.id) petIds.add(item.pet.id);
        });
        order.items.addOnServices.forEach(item => {
            if (item.orderItem.petId) petIds.add(item.orderItem.petId);
        });
        order.items.products.forEach(item => {
            if (item.orderItem.petId) petIds.add(item.orderItem.petId);
        });
        order.items.customs.forEach(item => {
            if (item.orderItem.petId) petIds.add(item.orderItem.petId);
        });

        return Array.from(petIds);
    };

    const transformOrderToAppointments = (order: Order): Array<AppointmentMultiple> => {
        const petIds = getUniquePetIds(order);
        const appointments: Array<AppointmentMultiple> = [];

        const services: Array<PetServiceWrapper> = petServices.map(petService => ({
            ...petService,
            duration: {
                hours: Math.trunc(petService.durationInMinutes / 60),
                minutes: petService.durationInMinutes % 60
            }
        }));

        petIds.forEach(petId => {
            const appointment: AppointmentMultiple = {
                clientId: order.client.id!,
                petId: petId,
                createdByStaffId: auth.user!.staffId,
                notes: order.notes,
                variableData: []
            };

            order.items.appointments.forEach(item => {
                if (item.pet.id === petId) {
                    const service = services.find(service => service.service.id === item.appointment.services[0].id);
                    appointment.variableData.push({
                        service: {
                            service: item.appointment.services[0],
                            price: Number(item.orderItem.amount),
                            durationInMinutes: service?.durationInMinutes || 0,
                            duration: {
                                hours: service?.durationInMinutes ? Math.trunc(service?.durationInMinutes / 60) : 0,
                                minutes: service?.durationInMinutes ? service?.durationInMinutes % 60 : 0
                            }
                        },
                        staffId: item.orderItem.staffId,
                        date: date,
                        type: itemTypes.service,
                        itemData: {
                            discount: {
                                type: item.orderItem.discount?.percentage ? 'percentage' : 'amount',
                                value: item.orderItem.discount?.percentage
                                    ? item.orderItem.discount.percentage
                                    : Number(item.orderItem.discount?.amount)
                            },
                            quantity: undefined,
                            price: Number(item.orderItem.amount),
                            total: Number(item.orderItem.total)
                        }
                    });
                }
            });

            order.items.addOnServices.forEach(item => {
                if (item.orderItem.petId === petId) {
                    appointment.variableData.push({
                        addOn: item.addOnService,
                        staffId: item.orderItem.staffId,
                        date: date,
                        type: itemTypes.addOn,
                        itemData: {
                            discount: {
                                type: item.orderItem.discount?.percentage ? 'percentage' : 'amount',
                                value: item.orderItem.discount?.percentage
                                    ? item.orderItem.discount.percentage
                                    : Number(item.orderItem.discount?.amount)
                            },
                            quantity: item.orderItem.quantity,
                            price: Number(item.orderItem.amount),
                            total: Number(item.orderItem.amount) * item.orderItem.quantity
                        }
                    });
                }
            });

            order.items.products.forEach(item => {
                if (item.orderItem.petId === petId) {
                    appointment.variableData.push({
                        product: item.product,
                        staffId: item.orderItem.staffId,
                        date: date,
                        type: itemTypes.product,
                        itemData: {
                            discount: {
                                type: item.orderItem.discount?.percentage ? 'percentage' : 'amount',
                                value: item.orderItem.discount?.percentage
                                    ? item.orderItem.discount.percentage
                                    : Number(item.orderItem.discount?.amount)
                            },
                            quantity: item.orderItem.quantity,
                            price: Number(item.orderItem.amount),
                            total: Number(item.orderItem.amount) * item.orderItem.quantity
                        }
                    });
                }
            });

            order.items.customs.forEach(item => {
                if (item.orderItem.petId === petId) {
                    appointment.variableData.push({
                        customDescription: item.orderItem.description,
                        staffId: item.orderItem.staffId,
                        date: date,
                        type: itemTypes.custom,
                        itemData: {
                            discount: {
                                type: item.orderItem.discount?.percentage ? 'percentage' : 'amount',
                                value: item.orderItem.discount?.percentage
                                    ? item.orderItem.discount.percentage
                                    : Number(item.orderItem.discount?.amount)
                            },
                            quantity: item.orderItem.quantity,
                            price: Number(item.orderItem.amount),
                            total: Number(item.orderItem.total)
                        }
                    });
                }
            });
            appointments.push(appointment);
        });

        return appointments;
    };

    const transformAppointmentOrderItemToAppointment = (
        appointmentItem: AppointmentOrderItem
    ): Array<AppointmentMultiple> => {
        const appointments: Array<AppointmentMultiple> = [];
        const now = moment().tz(timezone);

        const services: Array<PetServiceWrapper> = petServices.map(petService => ({
            ...petService,
            duration: {
                hours: Math.trunc(petService.durationInMinutes / 60),
                minutes: petService.durationInMinutes % 60
            }
        }));

        const service = services.find(service => service.service.id === appointmentItem.appointment.services[0].id);

        const appointment: AppointmentMultiple = {
            clientId: appointmentItem.appointment.customer.id!,
            petId: appointmentItem.orderItem.petId!,
            createdByStaffId: auth.user!.staffId,
            notes: appointmentItem.appointment.notes,
            variableData: [
                {
                    service: {
                        service: appointmentItem.appointment.services[0],
                        price: Number(appointmentItem.orderItem.amount),
                        durationInMinutes: service?.durationInMinutes || 0,
                        duration: {
                            hours: service?.durationInMinutes ? Math.trunc(service?.durationInMinutes / 60) : 0,
                            minutes: service?.durationInMinutes ? service?.durationInMinutes % 60 : 0
                        }
                    },
                    staffId: appointmentItem.orderItem.staffId,
                    date: now.isBefore(appointmentItem.appointment.duration.from)
                        ? appointmentItem.appointment.duration.from
                        : date,
                    type: itemTypes.service,
                    itemData: {
                        discount: {
                            type: appointmentItem.orderItem.discount?.percentage ? 'percentage' : 'amount',
                            value: appointmentItem.orderItem.discount?.percentage
                                ? appointmentItem.orderItem.discount.percentage
                                : Number(appointmentItem.orderItem.discount?.amount)
                        },
                        quantity: undefined,
                        price: Number(appointmentItem.orderItem.amount),
                        total: Number(appointmentItem.orderItem.total)
                    }
                }
            ]
        };
        appointments.push(appointment);

        return appointments;
    };

    const refreshHandler = () => {
        setShowPetCard(prev => ({ ...prev, petId: undefined }));
        dispatch(fetchPetsThunk(apiClientWrapper, parentId ?? 0));
    };

    useEffect(() => {
        if (props.at) {
            setDate(props.at);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.at]);

    useEffect(() => {
        if (
            !showComplementaryItem &&
            !showPetAndServices &&
            !appointmentCreated &&
            !orderData.showDetail &&
            !orderData.showCheckout &&
            !depositCollect
        ) {
            dispatch(
                fetchAlertsThunk(
                    apiClientWrapper,
                    parentId || 0,
                    petId || 0,
                    date,
                    activeFrequency,
                    service?.service?.id,
                    staffId,
                    props.appointmentId
                )
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [date]);

    const getQuantityItems = () => {
        let quantity = 0;
        multipleAppointments.forEach(app => {
            app.variableData?.forEach(data => {
                quantity++;
            });
        });
        return quantity;
    };

    useEffect(() => {
        props.onChangeTime &&
            props.onChangeTime(
                date,
                multipleAppointments.length > 0 ? serviceTotal?.durationInMinutes : service?.durationInMinutes,
                staffId
            );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [date, service, staffId, multipleAppointments, serviceTotal]);

    useEffect(() => {
        setServicesUsed(getServicesUsed());
        setItemsUsed(getItemsUsed());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [petId, type]);

    useEffect(() => {
        setExceededDeposit(
            marketplace.deposit.type === MarketplaceDepositType.Amount &&
                convertToNumber(marketplace.deposit.value || '0') * getQuantityItems() >
                    (serviceTotal?.price || service?.price || 0)
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [serviceTotal]);

    useEffect(() => {
        if (activeFrequency) {
            setIsCollectDeposit(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeFrequency]);

    useEffect(() => {
        if (isCollectDeposit) {
            setActiveFrequency(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isCollectDeposit]);

    useEffect(() => {
        if (haveMultiplePets()) {
            setFrequency(undefined);
            setActiveFrequency(false);
            setShowDepositAndNote(true);
        }
        if (multipleAppointments.length > 0) {
            let priceTotal = 0;
            let durationMinutesTotal = 0;
            let durationTotal: Duration = {
                hours: undefined,
                minutes: undefined
            };
            let minorDate = date;
            multipleAppointments.forEach((apt: AppointmentMultiple) => {
                apt.variableData?.forEach((apt: DataVaraible) => {
                    if (apt.date.isBefore(minorDate)) {
                        minorDate = apt.date;
                    }
                    priceTotal =
                        priceTotal +
                        (getPriceWithDiscount(apt, apt.service?.price) ||
                            getPriceWithDiscount(apt, apt.itemData?.total) ||
                            0);
                    durationMinutesTotal = durationMinutesTotal + (apt.service?.durationInMinutes || 0);
                });
            });
            durationTotal = {
                hours: Math.trunc(durationMinutesTotal / 60),
                minutes: durationMinutesTotal % 60
            };
            setServiceTotal({
                price: priceTotal,
                durationInMinutes: durationMinutesTotal,
                duration: durationTotal
            });
            setShowDepositAndNote(true);
            setDate(minorDate);
        } else {
            setServiceTotal(undefined);
            setShowDepositAndNote(false);
        }
        setPetsUsed(getPetsUsed());
        if (rebook && multipleAppointments.length > 0) {
            setServicesUsed(getServicesUsed());
            setRebook(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [multipleAppointments]);

    useEffect(() => {
        if (props.order) {
            setRebook(true);
            setLoading(true);
            const appointmentToRebook = props.order.items.appointments[0];
            setDate(
                moment().tz(timezone).isBefore(appointmentToRebook.appointment.duration.from)
                    ? appointmentToRebook.appointment.duration.from
                    : moment().tz(timezone)
            );
            setDateToEdit(
                moment().tz(timezone).isBefore(appointmentToRebook.appointment.duration.from)
                    ? appointmentToRebook.appointment.duration.from
                    : moment().tz(timezone)
            );
            setParentId(appointmentToRebook.appointment.customer.id);
            setPetId(appointmentToRebook.appointment.pet.id);
            setVersionPetAndService(undefined);
            dispatch(fetchPetServicesThunk(apiClientWrapper, appointmentToRebook.appointment.pet.id));
        }
        if (props.appointmentItem) {
            setRebook(true);
            setLoading(true);
            setDate(
                moment().tz(timezone).isBefore(props.appointmentItem.appointment.duration.from)
                    ? props.appointmentItem.appointment.duration.from
                    : moment().tz(timezone)
            );
            setDateToEdit(
                moment().tz(timezone).isBefore(props.appointmentItem.appointment.duration.from)
                    ? props.appointmentItem.appointment.duration.from
                    : moment().tz(timezone)
            );
            setVersionPetAndService(undefined);
            setParentId(props.appointmentItem.appointment.customer.id);
            setPetId(props.appointmentItem.appointment.pet.id);
            dispatch(fetchPetServicesThunk(apiClientWrapper, props.appointmentItem.appointment.pet.id));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.order, props.appointmentItem]);

    useNonInitialEffect(() => {
        switch (newBookingsStatus) {
            case NewBookingsStatus.SavingMultiple:
            case NewBookingsStatus.Saving:
                props.onStartBooking && props.onStartBooking();
                break;
            case NewBookingsStatus.SaveMultipleSuccess:
            case NewBookingsStatus.SaveSuccess:
                setSaving(false);
                setDepositCollect(false);
                setAppointmentCreated(true);
                props.onChangeNewBooking && props.onChangeNewBooking(false);
                break;
            case NewBookingsStatus.SaveForDepositSuccess:
            case NewBookingsStatus.SaveMultipleBookingForDepositSuccess:
                setDepositCollect(true);
                setSaving(false);
                setOrderData(prev => ({
                    ...prev,
                    orderId: savedBooking?.appointments[0]?.orderId,
                    bookingId: savedBooking?.id,
                    showDetail: false
                }));
                break;
            case NewBookingsStatus.FetchPetServicesSuccess:
                setLoading(false);
                if (props.order && rebook) {
                    setMultipleAppointments(transformOrderToAppointments(props.order));
                }
                if (props.appointmentItem && rebook) {
                    setMultipleAppointments(transformAppointmentOrderItemToAppointment(props.appointmentItem));
                }
                break;
            case NewBookingsStatus.FetchAlertsSuccess:
                const alertBusinessHour = alerts?.filter(alert => alert.alertType === 'date_time');
                if (alertBusinessHour && alertBusinessHour?.length > 0) {
                    setShowAlertBusinessHour(true);
                } else {
                    setShowAlertBusinessHour(false);
                }
                setLoading(false);
                break;
            case NewBookingsStatus.Error:
                setSaving(false);
                setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [newBookingsStatus]);

    useNonInitialEffect(() => {
        switch (bookingStatus) {
            case BookingsStatus.DeleteSuccess:
                setDepositCollect(false);
                setSaving(false);
                break;
            case BookingsStatus.Error:
                setSaving(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bookingStatus]);

    useEffect(() => {
        switch (serviceStatus) {
            case ServicesStatus.Fetching:
                setLoading(true);
                break;
            case ServicesStatus.FetchSuccess:
                setLoading(false);
                break;
            case ServicesStatus.Error:
                setLoading(false);
                break;
        }
    }, [serviceStatus]);

    useNonInitialEffect(() => {
        switch (appointmentsStatus) {
            case AppointmentsStatus.CountRecurrentSuccess:
                if (recurrentInfo?.frecuency) {
                    setFrequency(recurrentInfo.frecuency);
                }
                break;
            case AppointmentsStatus.Error:
                setSaving(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appointmentsStatus]);

    useNonInitialEffect(() => {
        setTouched(true);
        props.onChangeNewBooking && props.onChangeNewBooking(true);
    }, [staffId, parentId, date, activeFrequency]);

    useNonInitialEffect(() => {
        if (loadingAppointments) {
            setTimeout(() => setLoadingAppointments(false), 1000);
        }
    }, [loadingAppointments]);

    useEffect(() => {
        if (props.parentID && !props.fromClient && !props.petID) {
            dispatch(fetchPetsThunk(apiClientWrapper, props.parentID!));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.parentID]);

    useEffect(() => {
        dispatch(fetchServicesThunk());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const clientView = (
        <Box className={classes.sectionContainer}>
            <ClientSearch
                fromClient={props.fromClient}
                parentID={parentId as number}
                parentName={parentName as string}
                setPetParent={handlerParentID}
                onAddClient={addNewClient}
                setPetId={value => setPetId(value)}
                readOnly={!!(props.parentID && !props.clientAdded)}
            />
        </Box>
    );

    const petsView = (
        <Box className={clsx(classes.sectionContainer, classes.marginTop)}>
            <PetSearch
                hideSelect={true}
                parentID={parentId}
                petId={petId}
                readOnly={!!(props.petID && !props.petAdded)}
                handlerPet={() => {}}
                onAddPet={props.onAddPet}
                multiplePets={true}
            />
        </Box>
    );

    const dateView = (
        <DateSelector
            isCollectDeposit={isCollectDeposit}
            date={date}
            isRecurrent={activeFrequency}
            frequency={frequency}
            showAlertBusinessHour={showAlertBusinessHour}
            multiplePet={!!(multipleAppointments.length > 1 || multipleAppointments[0]?.variableData?.length > 1)}
            onChangeFrequencyDuration={handleFrequencyDuration}
            onChangeRecurrent={handleChangeRecurrency}
            onChangeDate={handlerChangeGeneralDate}
            readOnly={false}
            alerts={alerts?.filter(alert => alert.alertType === 'date_time' || alert.alertType === 'unified')}
        />
    );

    const depositView = (
        <DepositSelector
            active={isCollectDeposit}
            servicePrice={service?.price}
            alerts={alerts?.filter(alert => alert.alertType === 'deposit')}
            readOnly={false}
            onChangeDeposit={handleChangeDeposit}
        />
    );

    const notesView = (
        <Box className={classes.sectionContainer}>
            <Notes
                title="Booking Notes"
                placeholder="Notes here..."
                staffId={staffId}
                notes={notes}
                onChangeNote={(value: string) => setNotes(value)}
            />
        </Box>
    );

    const appointmentBookedView = (
        <Box className={classes.appointmentCreatedContainer}>
            <AppointmentCreated
                withDeposit={isCollectDeposit && !exceededDeposit}
                multipleAppointments={multipleAppointments}
                date={date}
                petId={petId}
                onGoToBooking={() => setOrderData(prev => ({ ...prev, showDetail: true }))}
            />
        </Box>
    );

    const multiplePetsView = (
        <Box>
            {loadingAppointments ? (
                <Spinner />
            ) : (
                <MultiplePets
                    date={date}
                    multipleAppointments={multipleAppointments}
                    onAddAnotherPet={handlerAddAnotherPet}
                    onAddService={(id: number) => handlerAddItem(id, itemTypes.service)}
                    onAddAddOn={(id: number) => {
                        handlerAddItem(id, itemTypes.addOn);
                    }}
                    onAddCustomItem={(id: number) => handlerAddItem(id, itemTypes.custom)}
                    onAddProduct={(id: number) => handlerAddItem(id, itemTypes.product)}
                    onDelete={handlerDeleteAppointmentsPet}
                    onEdit={handlerEditAppointmentPet}
                    onDeleteAppointment={handlerDeleteAppointmentPet}
                    onShowActions={(
                        petIdShow: number,
                        dateShow: Moment,
                        itemDataShow?: ItemData,
                        serviceShow?: PetServiceWrapper,
                        staffIdShow?: number,
                        productShow?: Product,
                        addOnShow?: AddOnService,
                        customShow?: string,
                        typeShow?: itemTypes
                    ) =>
                        setShowActions({
                            petId: petIdShow,
                            service: serviceShow,
                            staffId: staffIdShow,
                            itemData: itemDataShow,
                            date: dateShow,
                            product: productShow,
                            addOn: addOnShow,
                            custom: customShow,
                            type: typeShow
                        })
                    }
                    onShowPetCard={(petIdSelected: number) =>
                        props.onShowPetCard && props.onShowPetCard(petIdSelected, parentId!)
                    }
                />
            )}
        </Box>
    );

    const petAndServiceView = (
        <PetAndServices
            parentId={parentId!}
            petId={petId}
            service={service}
            staffId={staffId}
            staffIdPreselected={props.staffId}
            discount={discount}
            servicesUsed={servicesUsed}
            petsUsed={petsUsed}
            versionPetAndServices={versionPetAndService}
            date={dateToEdit ?? date}
            petWithoutSelect={
                versionPetAndService !== versionPetAndServices.add &&
                versionPetAndService !== versionPetAndServices.another
            }
            onBack={backPetAndServicesOrComplementaryItem}
            onBook={handlerSavePetAndServices}
            onRemove={handlerRemoveAppointments}
        />
    );

    const complementaryItemView = (
        <ComplementaryItem
            parentId={parentId!}
            petId={petId}
            type={type}
            versionPetAndServices={versionPetAndService}
            date={dateToEdit ?? date}
            product={product}
            addOn={addOn}
            custom={custom}
            staffId={staffId}
            quantity={quantity}
            amount={amount}
            discount={discount}
            itemsUsed={itemsUsed}
            petWithoutSelect={true}
            dates={datesAddOn}
            onBack={backPetAndServicesOrComplementaryItem}
            onBook={handlerSaveItems}
            onRemove={handlerRemoveItems}
        />
    );

    const orderDetailView = (
        <OrderDetail
            orderId={orderData.orderId!}
            bookingId={orderData.bookingId!}
            onClose={handleCloseBooking}
            onCheckout={(bookingId, orderId) =>
                setOrderData(prev => ({ ...prev, bookingId, orderId, showDetail: false, showCheckout: true }))
            }
        />
    );

    const orderCheckoutView = (
        <Checkout
            orderId={orderData.orderId!}
            bookingId={orderData.bookingId!}
            type="booking"
            onClose={handleCloseBooking}
            onBack={() => setOrderData(prev => ({ ...prev, showDetail: true, showCheckout: false }))}
        />
    );

    const confirmCloseDialog = (
        <ConfirmClosePopup
            title="Exit Booking"
            question="Your changes will not be saved. Are you sure you want to exit this booking?"
            onDiscard={() =>
                setClosing(prev => ({
                    ...prev,
                    close: false,
                    tabChange: false
                }))
            }
            onSave={() => handleClose(true)}
        />
    );

    return (
        <Box
            className={clsx(classes.container, {
                [classes.withoutEvents]: saving
            })}
        >
            {loading ? (
                <Box className={classes.loadingContainer}>
                    <CircularProgress
                        size={isMobile ? '1.5rem' : '2.5rem'}
                        style={{ color: '#EAB464' }}
                    />
                </Box>
            ) : (
                <>
                    {!appointmentCreated && !showPetAndServices && !showComplementaryItem && (
                        <Box className={classes.headerContainer}>
                            <CreateBookingHeader
                                title={depositCollect ? 'Collect Deposit' : appointmentCreated ? '' : 'Add Appointment'}
                                depositCollect={depositCollect}
                                onClose={
                                    showPetAndServices || showComplementaryItem
                                        ? undefined
                                        : appointmentCreated
                                        ? handleBookedBooking
                                        : !touched && isMobile
                                        ? handleCloseWithOutModal
                                        : isMobile
                                        ? handleClosing
                                        : undefined
                                }
                                onBack={
                                    depositCollect
                                        ? backHandler
                                        : showPetAndServices || showComplementaryItem
                                        ? backPetAndServicesOrComplementaryItem
                                        : undefined
                                }
                                onShowBlockedTime={
                                    props.onShowBlockedTime && !appointmentCreated ? changeTabHandler : undefined
                                }
                            />
                        </Box>
                    )}
                    {orderData.showDetail ? (
                        orderDetailView
                    ) : orderData.showCheckout ? (
                        orderCheckoutView
                    ) : appointmentCreated ? (
                        appointmentBookedView
                    ) : showPetAndServices ? (
                        petAndServiceView
                    ) : showComplementaryItem ? (
                        complementaryItemView
                    ) : (
                        <OverlayScrollbarsComponent style={{ height: '100%' }}>
                            <Box className={classes.appointmentContainer}>
                                {depositCollect ? (
                                    <CollectDeposit
                                        bookingId={savedBooking!.id!}
                                        onClose={handleCloseBooking}
                                        onStartBooking={props.onStartBooking}
                                        onCollected={handlerCollected}
                                    />
                                ) : (
                                    <Fragment>
                                        {dateView}
                                        {clientView}
                                        {parentId &&
                                            petsState.list.filter(pet => !pet.deleted && !pet.deceased).length === 0 &&
                                            petsView}
                                        {parentId &&
                                            petsState.list.filter(pet => !pet.deleted && !pet.deceased).length > 0 &&
                                            multiplePetsView}
                                        {showDepositAndNote && notesView}
                                        {showDepositAndNote &&
                                            !exceededDeposit &&
                                            marketplace.deposit.type !== MarketplaceDepositType.None &&
                                            depositView}
                                    </Fragment>
                                )}
                            </Box>
                        </OverlayScrollbarsComponent>
                    )}
                    {!depositCollect &&
                        !showPetAndServices &&
                        !showComplementaryItem &&
                        !(isCollectDeposit && !exceededDeposit && appointmentCreated) && (
                            <Box
                                className={clsx(classes.totalContainer, {
                                    [classes.marginTotalContainer]: !appointmentCreated
                                })}
                            >
                                <FooterComponent
                                    success={appointmentCreated}
                                    withTotal={!appointmentCreated}
                                    title={'Total'}
                                    loading={saving}
                                    total={
                                        appointmentCreated
                                            ? undefined
                                            : multipleAppointments.length > 0
                                            ? serviceTotal?.price
                                            : service?.price
                                    }
                                    hours={serviceTotal?.duration?.hours}
                                    minutes={serviceTotal?.duration?.minutes}
                                    buttonText={
                                        isCollectDeposit && !appointmentCreated && !exceededDeposit
                                            ? 'Pay & Book Appoinment'
                                            : appointmentCreated
                                            ? 'Done'
                                            : 'Book Appoinment'
                                    }
                                    disabledButton={
                                        multipleAppointments.length === 0 ||
                                        (activeFrequency && !frequency) ||
                                        (showPetAndServices && (!petId || !service || !staffId)) ||
                                        withoutService()
                                    }
                                    deposit={undefined}
                                    onBook={handleBooked}
                                />
                            </Box>
                        )}
                    {(closing.close || closing.tabChange) && confirmCloseDialog}
                    {showActions?.petId && (
                        <ActionsPopup
                            actions={[
                                {
                                    label: getNameItem(showActions.type!),
                                    icon: faPencil,
                                    onClick: () =>
                                        handlerEditAppointmentPet(
                                            showActions.petId!,
                                            showActions.date!,
                                            showActions.staffId,
                                            showActions.itemData,
                                            showActions.service,
                                            showActions.product,
                                            showActions.addOn,
                                            showActions.custom,
                                            showActions.type
                                        )
                                },
                                {
                                    label: 'Delete',
                                    icon: faTrashAlt,
                                    color: '#EF4F57',
                                    onClick: () =>
                                        handlerDeleteAppointmentPet(
                                            showActions.petId!,
                                            getItemToDelete(showActions.type!),
                                            showActions.type,
                                            showActions.date
                                        )
                                }
                            ]}
                            onClose={() =>
                                setShowActions({
                                    date: undefined,
                                    staffId: undefined,
                                    service: undefined,
                                    petId: undefined,
                                    product: undefined,
                                    addOn: undefined,
                                    custom: undefined,
                                    type: undefined,
                                    discount: undefined,
                                    itemData: undefined
                                })
                            }
                        />
                    )}
                </>
            )}
        </Box>
    );
};

export default CreateBooking;
